import { useState } from "react";
import type { ConflictWarning } from "@alpha/engine";
import { api, apiErrorMessage } from "../lib/api";
import { useAdminMutation } from "../lib/adminData";
import { isoToWallClock, wallClockToIso } from "../lib/datetime";
import { CUSTOM_KEY, groupLocationOptions, locationOptions, matchLocation } from "../lib/gameLocation";
import type { Bundle, Game } from "../lib/types";
import { Badge, Input, Select } from "./ui";

export const CONFLICT_LABEL: Record<ConflictWarning["type"], string> = {
  teamOverlap: "Team overlap",
  insufficientRest: "Short rest",
  fieldClash: "Field double-booked",
};

/**
 * When + where for one game (spec §8.6, §10). Times are typed as wall clock in
 * the tournament's own timezone and stored UTC. Picking a venue's named field
 * sets `venueId` and `field` together, which is what makes the engine's
 * field-clash check meaningful; Custom keeps ad-hoc sites possible.
 */
export function GameScheduleLine({
  tid,
  bundle,
  game,
  warnings,
}: {
  tid: string;
  bundle: Bundle;
  game: Game;
  warnings: ConflictWarning[];
}) {
  const tz = bundle.tournament.timezone;
  const options = locationOptions(
    bundle.venues,
    game.venueId ? [...bundle.tournament.venueIds, game.venueId] : bundle.tournament.venueIds,
  );

  const [when, setWhen] = useState(() => isoToWallClock(game.scheduledAt, tz));
  const [pick, setPick] = useState(() => matchLocation(options, game));
  const [custom, setCustom] = useState(game.field ?? "");
  const [error, setError] = useState<string>();
  const refresh = useAdminMutation(tid, () => Promise.resolve());

  const save = (patch: Record<string, string | null>) =>
    api
      .put(`/admin/tournaments/${tid}/games/${game.id}`, patch)
      .then(() => {
        setError(undefined);
        refresh.mutate();
      })
      .catch((e) => setError(apiErrorMessage(e, "Could not save this game.")));

  const commitTime = () => {
    if (when === isoToWallClock(game.scheduledAt, tz)) return;
    if (when === "") {
      void save({ scheduledAt: null });
      return;
    }
    const iso = wallClockToIso(when, tz);
    if (iso === null) {
      setError("Enter a valid date and time.");
      return;
    }
    void save({ scheduledAt: iso });
  };

  const commitCustom = () => {
    const next = custom.trim();
    if (next === (game.field ?? "")) return;
    void save({ venueId: null, field: next || null });
  };

  const choose = (key: string) => {
    setPick(key);
    if (key === CUSTOM_KEY) {
      // Custom means "not at an attached venue" (spec §8.6), so detach now rather
      // than waiting on a text edit that may never come — leaving the text alone
      // for the admin to keep or replace.
      if (game.venueId) void save({ venueId: null });
      return;
    }
    const option = options.find((o) => o.key === key);
    if (!option) return;
    setCustom(option.field ?? "");
    void save({ venueId: option.venueId ?? null, field: option.field ?? null });
  };

  return (
    <div className="flex basis-full flex-wrap items-center gap-2 border-t border-slate-100 pt-2">
      <Input
        type="datetime-local"
        uiSize="sm"
        className="w-44 shrink-0"
        aria-label={`Start time (${tz})`}
        value={when}
        onChange={(e) => setWhen(e.target.value)}
        onBlur={commitTime}
      />
      <Select
        uiSize="sm"
        className="w-48 shrink-0"
        aria-label="Location"
        value={pick}
        onChange={(e) => choose(e.target.value)}
      >
        {groupLocationOptions(options).map((g, gi) =>
          g.group === null ? (
            g.options.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))
          ) : (
            <optgroup key={`${g.group}-${gi}`} label={g.group}>
              {g.options.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </optgroup>
          ),
        )}
      </Select>
      {pick === CUSTOM_KEY ? (
        <Input
          uiSize="sm"
          className="w-40 shrink-0"
          aria-label="Field name"
          placeholder="Field 1"
          maxLength={60}
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onBlur={commitCustom}
        />
      ) : null}
      {warnings.map((w, i) => (
        <Badge key={i} tone="amber">
          {CONFLICT_LABEL[w.type]}
        </Badge>
      ))}
      {error ? <span className="basis-full text-xs text-red-600">{error}</span> : null}
    </div>
  );
}
